// Entry point. Wires state machine, TTS, timer, recorder, Groq, Media Session, and DOM.

import { createStore, Status } from './state.js'
import { parseReport, questionNumber } from './report.js'
import * as tts from './tts.js'
import * as timer from './timer.js'
import * as mediasession from './mediasession.js'
import { Recorder } from './recorder.js'
import * as groq from './groq.js'
import * as github from './github.js'
import * as yaml from 'js-yaml'
import { render } from './ui.js'
import { SAMPLE_REPORT } from './sample-report.js'

document.getElementById('build-version').textContent = __BUILD_TIME__

// ── parse report ─────────────────────────────────────────────────────────────

const { report } = parseReport(SAMPLE_REPORT)

// ── store ─────────────────────────────────────────────────────────────────────

const store = createStore(report)
const recorder = new Recorder()

// ── media session ─────────────────────────────────────────────────────────────

mediasession.init(action => store.dispatch(action))

// ── settings ──────────────────────────────────────────────────────────────────

const settingsDialog = document.getElementById('settings-dialog')
const apiKeyInput    = document.getElementById('api-key-input')

function openSettings() {
  apiKeyInput.value = groq.getApiKey()
  document.getElementById('gh-token-input').value = github.getToken()
  document.getElementById('gh-repo-input').value  = github.getRepo()
  settingsDialog.showModal()
}

document.getElementById('settings-btn').addEventListener('click', openSettings)
document.getElementById('picker-settings-btn').addEventListener('click', openSettings)

document.getElementById('save-settings').addEventListener('click', () => {
  groq.setApiKey(apiKeyInput.value)
  github.setToken(document.getElementById('gh-token-input').value)
  github.setRepo(document.getElementById('gh-repo-input').value)
  settingsDialog.close()
})

document.getElementById('cancel-settings').addEventListener('click', () => {
  settingsDialog.close()
})

document.getElementById('test-settings').addEventListener('click', async () => {
  const groqKey = apiKeyInput.value
  const ghToken = document.getElementById('gh-token-input').value
  const ghRepo  = document.getElementById('gh-repo-input').value
  const groqEl  = document.getElementById('groq-status')
  const ghEl    = document.getElementById('github-status')

  if (groqKey) {
    groqEl.textContent = 'testing…'
    groqEl.className = 'field-status'
    try {
      await groq.testApiKey(groqKey)
      groqEl.textContent = '✓ valid'
      groqEl.className = 'field-status ok'
    } catch (err) {
      groqEl.textContent = `✗ ${err.message}`
      groqEl.className = 'field-status err'
    }
  } else {
    groqEl.textContent = '(not set)'
    groqEl.className = 'field-status'
  }

  if (ghToken && ghRepo) {
    ghEl.textContent = 'testing…'
    ghEl.className = 'field-status'
    try {
      const msg = await github.testConnection(ghToken, ghRepo)
      ghEl.textContent = `✓ ${msg}`
      ghEl.className = 'field-status ok'
    } catch (err) {
      ghEl.textContent = `✗ ${err.message}`
      ghEl.className = 'field-status err'
    }
  } else {
    ghEl.textContent = '(not configured)'
    ghEl.className = 'field-status'
  }
})

// Error banner dismiss
document.getElementById('error-dismiss').addEventListener('click', () => {
  store.dispatch({ type: 'CLEAR_ERROR' })
})

// ── splash ────────────────────────────────────────────────────────────────────

let splashDone = false

document.getElementById('splash').addEventListener('click', async () => {
  splashDone = true

  // Unlock iOS TTS within user gesture. Guard in case API is unavailable.
  try {
    const unlock = new SpeechSynthesisUtterance('')
    speechSynthesis.speak(unlock)
    speechSynthesis.cancel()
  } catch (e) {
    console.warn('TTS unlock failed:', e)
  }

  document.getElementById('splash').hidden = true

  if (github.isConfigured()) {
    await _showPicker()
  } else {
    _boot()
  }
}, { once: true })

async function _showPicker() {
  const picker = document.getElementById('report-picker')
  const listEl = document.getElementById('report-list')
  picker.hidden = false
  listEl.innerHTML = '<p class="picker-hint">Loading reports…</p>'

  let reports
  try {
    reports = await github.listReports()
  } catch (err) {
    listEl.innerHTML = `<p class="picker-hint">Error: ${err.message}</p>`
    return
  }

  if (!reports.length) {
    listEl.innerHTML = '<p class="picker-hint">No reports in outbox.</p>'
    return
  }

  listEl.innerHTML = ''
  for (const r of reports) {
    const btn = document.createElement('button')
    btn.className = 'report-item'
    btn.innerHTML =
      `<span class="report-item-project">${r.project}</span>` +
      `<span class="report-item-slug">${r.slug}</span>` +
      `<span class="report-item-date">${r.date}</span>`
    btn.addEventListener('click', async () => {
      listEl.innerHTML = '<p class="picker-hint">Loading…</p>'
      try {
        const yamlText = await github.fetchReport(r.path)
        listEl.innerHTML = '<p class="picker-hint">Parsing…</p>'
        const { report: fetched, error } = parseReport(yaml.load(yamlText))
        if (!fetched) throw new Error(`Parse error: ${error}`)
        store.dispatch({ type: 'LOAD_REPORT', report: fetched })
        picker.hidden = true
        _boot()
      } catch (err) {
        const msg = `Failed to load report: ${err.message}`
        listEl.innerHTML = `<p class="picker-hint" style="color:#ff6b6b">${msg}</p>`
        store.dispatch({ type: 'SET_ERROR', message: msg })
      }
    })
    listEl.appendChild(btn)
  }
}

function _boot() {
  document.getElementById('app').hidden = false
  store.dispatch({ type: 'BOOT' })
  mediasession.activate().catch(e => console.warn('MediaSession activate failed:', e))
}

// ── state changes → render + side effects ─────────────────────────────────────

store.subscribe((state, prev) => {
  render(state)
  _runEffects(state, prev)
})

render(store.getState())

// ── side effects ──────────────────────────────────────────────────────────────

function _runEffects(state, prev) {
  const statusChanged = state.status !== prev.status
  const indexChanged  = state.sectionIndex !== prev.sectionIndex
  const histChanged   = state.historyIndex !== prev.historyIndex

  // ── TTS: speak current section ───────────────────────────────────────────
  if (state.status === Status.SPEAKING && (statusChanged || indexChanged)) {
    tts.stop()
    timer.stop()
    const section = state.sections[state.sectionIndex]
    if (section) {
      const qNum = questionNumber(state.sections, state.sectionIndex)
      mediasession.setMetadata(
        section.title ?? section.prompt ?? '',
        state.report?.project ?? '',
        state.report?.title ?? '',
      )
      tts.speak(tts.sectionText(section, qNum), {
        onEnd: () => store.dispatch({ type: 'TTS_ENDED' }),
      })
    }
  }

  // ── TTS: speak history item (with any saved response) ───────────────────
  if (state.status === Status.HISTORY_MODE && (statusChanged || histChanged)) {
    tts.stop()
    timer.stop()
    const idx = state.historyIndex ?? 0
    const section  = state.sections[idx]
    const response = state.responses[section?.id] ?? null
    if (section) {
      const qNum = questionNumber(state.sections, idx)
      mediasession.setMetadata(
        `History: ${section.title ?? section.prompt ?? ''}`,
        state.report?.project ?? '',
        '',
      )
      // History TTS has no onEnd dispatch — just reads and waits for gesture
      tts.speak(tts.historyText(section, response, qNum))
    }
  }

  // ── TTS: read back transcript when entering CONFIRMING ───────────────────
  if (state.status === Status.CONFIRMING && statusChanged) {
    tts.stop()
    tts.speak(tts.readbackText(state.pendingTranscript ?? ''))
    // Confirm timer starts immediately alongside the readback
  }

  // ── TTS: stop when pausing ───────────────────────────────────────────────
  if (state.status === Status.PAUSED && statusChanged) {
    tts.stop()
    timer.stop()
  }

  // ── Timer: start COUNTING_DOWN ───────────────────────────────────────────
  if (state.status === Status.COUNTING_DOWN && statusChanged) {
    timer.start(state.timerRemaining, {
      onTick: () => store.dispatch({ type: 'TIMER_TICK' }),
      onDone: () => store.dispatch({ type: 'TIMER_DONE' }),
    })
  }

  // ── Timer: start CONFIRMING ──────────────────────────────────────────────
  if (state.status === Status.CONFIRMING && statusChanged) {
    timer.start(state.timerRemaining, {
      onTick: () => store.dispatch({ type: 'TIMER_TICK' }),
      onDone: () => store.dispatch({ type: 'TIMER_DONE' }),
    })
  }

  // ── Timer: stop when leaving a timed state ───────────────────────────────
  if (prev.status === Status.COUNTING_DOWN || prev.status === Status.CONFIRMING) {
    if (state.status !== prev.status &&
        state.status !== Status.PAUSED) {
      timer.stop()
    }
  }

  // ── Recorder: start when entering RECORDING ─────────────────────────────
  if (state.status === Status.RECORDING && statusChanged) {
    tts.stop()
    timer.stop()
    recorder.start().catch(err => {
      store.dispatch({ type: 'TRANSCRIPT_ERROR', message: `Microphone error: ${err.message}` })
    })
  }

  // ── Recorder: stop and transcribe when entering TRANSCRIBING ────────────
  if (state.status === Status.TRANSCRIBING && prev.status === Status.RECORDING) {
    const section  = state.sections[state.sectionIndex]
    const context  = groq.buildContext(section)
    recorder.stop()
      .then(blob  => groq.transcribe(blob, context))
      .then(text  => store.dispatch({ type: 'TRANSCRIPT_READY', transcript: text }))
      .catch(err  => store.dispatch({ type: 'TRANSCRIPT_ERROR', message: err.message }))
  }

  // ── Recorder: cancel if we left RECORDING without transcribing ──────────
  if (prev.status === Status.RECORDING && state.status !== Status.TRANSCRIBING) {
    if (recorder.isRecording) recorder.cancel()
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  if (state.status === Status.DONE && statusChanged) {
    tts.stop()
    timer.stop()
    tts.speak(`Session complete. You reviewed ${state.sections.length} sections.`)
    if (github.isConfigured() && state.report?.id) {
      github.submitResponse(state.report.id, state.report.project ?? '', state.responses)
        .catch(err => store.dispatch({ type: 'SET_ERROR', message: `Submit failed: ${err.message}` }))
    }
  }
}

// ── tap counter ───────────────────────────────────────────────────────────────

let tapCount = 0
let tapTimeout = null

function handleTap() {
  tapCount++
  clearTimeout(tapTimeout)
  tapTimeout = setTimeout(() => {
    const { status } = store.getState()

    if (tapCount === 1) {
      store.dispatch({ type: 'PLAY_PAUSE' })

    } else if (tapCount === 2) {
      if (status === Status.RECORDING) {
        store.dispatch({ type: 'RECORD_CANCEL' })
      } else {
        store.dispatch({ type: 'NEXT' })
      }

    } else if (tapCount >= 3) {
      store.dispatch({ type: 'PREV' })
    }

    tapCount = 0
  }, 350)
}

document.getElementById('main').addEventListener('click', e => {
  if (!splashDone) return
  if (e.target.closest('.option-btn')) return
  handleTap()
})

// Option button taps → ANSWER
document.getElementById('options-list').addEventListener('click', e => {
  const btn = e.target.closest('.option-btn')
  if (!btn) return
  const optionId = btn.dataset.optionId
  const { sections, sectionIndex } = store.getState()
  const section = sections[sectionIndex]
  const text = section?.options?.find(o => o.id === optionId)?.text ?? ''
  store.dispatch({ type: 'ANSWER', selected: optionId, transcript: text })
})

// Keyboard shortcuts for desktop development
document.addEventListener('keydown', e => {
  if (!splashDone) return
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

  if (e.key === ' ')          { e.preventDefault(); store.dispatch({ type: 'PLAY_PAUSE' }) }
  if (e.key === 'ArrowRight') store.dispatch({ type: 'NEXT' })
  if (e.key === 'ArrowLeft')  store.dispatch({ type: 'PREV' })
  if (e.key === 'Escape' && store.getState().status === Status.RECORDING) {
    store.dispatch({ type: 'RECORD_CANCEL' })
  }
  // Number keys for multiple choice
  if (e.key >= '1' && e.key <= '9') {
    const { sections, sectionIndex, status } = store.getState()
    if (status !== Status.WAITING_ANSWER) return
    const section = sections[sectionIndex]
    if (section?.kind !== 'multiple_choice') return
    const opt = section.options?.[parseInt(e.key, 10) - 1]
    if (opt) store.dispatch({ type: 'ANSWER', selected: opt.id, transcript: opt.text })
  }
})
